// TVP-B3 fix: synthetic spread mặc định 2bps (10bps trên giá lớn trông như bug,
// ví dụ 77k spread trên BTC). Có tick provider thì luôn dùng spread = 1 tick.
const DEFAULT_SPREAD_BPS = 2;
function toTimestamp(date) {
    return date instanceof Date ? date.getTime() : date;
}
// F-02b: số lẻ thập phân của một tick (tick 0.01 -> 2, tick 50 -> 0).
// Epsilon 1e-9 triệt sai số IEEE-754 ở biên (VD: 102.66/0.01 thiếu 2e-12).
function decimalsForTick(tick) {
    if (!Number.isFinite(tick) || tick <= 0)
        return 2;
    return Math.max(0, Math.ceil(-Math.log10(tick) - 1e-9));
}
// F-02b: làm tròn XUỐNG theo tick (dùng cho Bid). Cộng 1e-9 trước khi floor
// để giá trị đúng biên tick (102.66/0.01 = 10265.999999999998) không rớt 1 tick.
function floorToTick(value, tick) {
    if (!Number.isFinite(value) || !Number.isFinite(tick) || tick <= 0)
        return value;
    const decimals = decimalsForTick(tick);
    const ticks = Math.floor(value / tick + 1e-9);
    return Number((ticks * tick).toFixed(decimals));
}
/**
 * Tính synthetic quote từ last price. Quy tắc xác định (deterministic):
 * - last không hợp lệ (<= 0 / NaN): spread = 0, bid = ask = last.
 * - có tickSizeProvider: spread = đúng 1 tick, bid/ask làm tròn theo tick.
 * - ngược lại: spread = last * spreadBps / 10000, bid/ask giữ nguyên.
 */
export function computeSyntheticQuote(symbol, lastPrice, timestamp, config = {}) {
    const bps = config.spreadBps ?? DEFAULT_SPREAD_BPS;
    if (!Number.isFinite(lastPrice) || lastPrice <= 0) {
        return {
            symbol,
            lastPrice,
            bidPrice: lastPrice,
            askPrice: lastPrice,
            spread: 0,
            spreadBps: 0,
            isSyntheticSpread: true,
            timestamp,
        };
    }
    const tick = config.tickSizeProvider?.(symbol, lastPrice);
    const spread = tick != null && tick > 0 ? tick : (lastPrice * bps) / 10000;
    // F-02b: bid = floor theo tick, ask = bid + tick (bất biến ask - bid === tick).
    // Math.round cả 2 phía làm bid === ask khi half-tick rớt cùng phía (SOL 102.66).
    if (tick != null && tick > 0) {
        const decimals = decimalsForTick(tick);
        const bid = floorToTick(lastPrice, tick);
        // Bất biến cấu trúc: ask - bid === tick ở mức decimal của tick
        // (float thô có thể dư ~5e-12, đã chuẩn hóa bằng toFixed).
        const ask = Number((bid + tick).toFixed(decimals));
        return {
            symbol,
            lastPrice,
            bidPrice: bid,
            askPrice: ask,
            spread: tick,
            spreadBps: (tick / lastPrice) * 10000,
            isSyntheticSpread: true,
            timestamp,
        };
    }
    const half = spread / 2;
    const bid = lastPrice - half;
    const ask = lastPrice + half;
    return {
        symbol,
        lastPrice,
        bidPrice: bid,
        askPrice: ask,
        spread,
        spreadBps: (spread / lastPrice) * 10000,
        isSyntheticSpread: true,
        timestamp,
    };
}
export class MarketDataPort {
    config;
    latest = new Map();
    subscribers = new Map();
    constructor(config = {}) {
        this.config = config;
    }
    getLatestQuote(symbol) {
        return this.latest.get(symbol);
    }
    feedBar(symbol, bar) {
        const quote = computeSyntheticQuote(symbol, bar.close, toTimestamp(bar.date), this.config);
        this.latest.set(symbol, quote);
        this.subscribers.get(symbol)?.forEach((cb) => cb(quote));
    }
    subscribeQuote(symbol, callback) {
        let set = this.subscribers.get(symbol);
        if (!set) {
            set = new Set();
            this.subscribers.set(symbol, set);
        }
        set.add(callback);
        return () => {
            set?.delete(callback);
        };
    }
}
export function createMarketDataPort(config = {}) {
    return new MarketDataPort(config);
}
